import React, { useMemo } from 'react';
import { addLocale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';

import MachineOperationalClosures from './MachineOperationalClosures';
import { dateFromLocalIso, dateToLocalIso } from '../machineOperationalClosures';

import {
	OPERATIONAL_SCHEDULE_DAYS,
	getOperationalScheduleFirstAllowedDate,
	getIanaTimezoneOptions,
} from '../machineOperationalSchedule';

const DEFAULT_OPENING_TIME = '09:00';
const DEFAULT_CLOSING_TIME = '17:00';
const OPERATIONAL_SCHEDULE_CALENDAR_LOCALE = 'en-monday';

addLocale(OPERATIONAL_SCHEDULE_CALENDAR_LOCALE, { firstDayOfWeek: 1 });

const MachineOperationalScheduleSettings = ({
	value,
	validation,
	loading,
	loadError,
	isNew,
	closures,
	closuresLoading,
	closuresLoadError,
	onChange,
	onRetry,
	onCreateClosure,
	onUpdateClosure,
	onDeleteClosure,
	onRetryClosures,
}) => {
	const timezone = value ? value.timezone : undefined;
	const timezoneOptions = useMemo(getIanaTimezoneOptions, []);
	const firstAllowedDate = useMemo(
		() => getOperationalScheduleFirstAllowedDate(timezone),
		[timezone],
	);
	const windowsByDay = useMemo(
		() => new Map((value?.windows || []).map((window) => [window.dayOfWeek, window])),
		[value],
	);

	const prepareForScheduleChange = (schedule, timezone = schedule.timezone) => {
		const firstAllowedDateAsLocal = dateToLocalIso(getOperationalScheduleFirstAllowedDate(timezone));
		if (schedule.effectiveFromLocal >= firstAllowedDateAsLocal) return schedule;
		return {
			...schedule,
			effectiveFromLocal: firstAllowedDateAsLocal,
			effectiveUntilLocal: schedule.effectiveUntilLocal >= firstAllowedDateAsLocal
				? schedule.effectiveUntilLocal
				: null,
		};
	};
	const updateTimezone = (timezone) => onChange(prepareForScheduleChange({ ...value, timezone }, timezone));
	const updateEffectiveDates = (dates) => {
		const effectiveFromLocal = dateToLocalIso(dates?.[0]);
		const effectiveUntilLocal = dates?.[1] ? dateToLocalIso(dates[1]) : null;
		onChange({ ...value, effectiveFromLocal, effectiveUntilLocal, runsIndefinitely: false });
	};
	const setRunsIndefinitely = (runsIndefinitely) => {
		const editableSchedule = prepareForScheduleChange(value);
		onChange({
			...editableSchedule,
			runsIndefinitely,
			effectiveUntilLocal: runsIndefinitely ? null
				: editableSchedule.effectiveUntilLocal,
		});
	};
	const updateWindow = (dayOfWeek, updates) => {
		const editableSchedule = prepareForScheduleChange(value);
		const currentWindow = windowsByDay.get(dayOfWeek);
		const nextWindows = currentWindow
			? editableSchedule.windows.map((window) => (window.dayOfWeek === dayOfWeek ? { ...window, ...updates } : window))
			: [
				...editableSchedule.windows,
				{
					dayOfWeek,
					opensAtLocal: DEFAULT_OPENING_TIME,
					closesAtLocal: DEFAULT_CLOSING_TIME,
					endsNextDay: false,
					...updates,
				},
			];
		onChange({ ...editableSchedule, windows: nextWindows });
	};

	const setDayEnabled = (dayOfWeek, enabled) => {
		if (enabled) {
			updateWindow(dayOfWeek, {});
			return;
		}
		const editableSchedule = prepareForScheduleChange(value);
		onChange({
			...editableSchedule,
			windows: editableSchedule.windows.filter((window) => window.dayOfWeek !== dayOfWeek),
		});
	};

	if (loading) {
		return <div className="machine-operational-schedule-loading">Loading operational schedule…</div>;
	}

	if (loadError) {
		return (
			<div className="machine-operational-schedule-load-error">
				<span>Operational schedule could not be loaded.</span>
				<Button type="button" label="Retry" icon="pi pi-refresh" onClick={onRetry} className="p-button-sm" />
			</div>
		);
	}

	return (
		<div className="machine-operational-schedule">
			{isNew && (
				<div className="machine-operational-schedule-new-note">
					No operational schedule has been configured yet. Set the local timezone and the days the machine operates.
				</div>
			)}
			<div className="flex flex-column gap-2 mb-4">
				<label htmlFor="operational-schedule-timezone" className="text-sm font-medium text-700">
					IANA timezone
				</label>
				<Dropdown
					inputId="operational-schedule-timezone"
					value={value.timezone}
					options={timezoneOptions}
					onChange={(event) => updateTimezone(event.value)}
					placeholder="Europe/Brussels"
					optionLabel="label"
					optionValue="value"
					filter
					editable
					className={`w-full ${validation.errors.timezone ? 'p-invalid' : ''}`}
				/>
				<small className={validation.errors.timezone ? 'p-error' : 'text-xs text-500'}>
					{validation.errors.timezone || 'Used to interpret all opening and closing times, including daylight-saving changes.'}
				</small>
			</div>

			<div className="machine-operational-schedule-period mb-4">
				<div className="grid">
					<div className="col-12 md:col-8">
						<label htmlFor="operational-schedule-dates" className="text-sm font-medium text-700 block mb-2">Opening-hours period</label>
						<Calendar
							inputId="operational-schedule-dates"
							value={[
								dateFromLocalIso(value.effectiveFromLocal),
								value.effectiveUntilLocal ? dateFromLocalIso(value.effectiveUntilLocal) : null,
							]}
							onChange={(event) => updateEffectiveDates(event.value)}
							selectionMode="range"
							minDate={firstAllowedDate}
							readOnlyInput
							showIcon
							showButtonBar
							dateFormat="dd M yy"
							locale={OPERATIONAL_SCHEDULE_CALENDAR_LOCALE}
							disabled={!value.effectiveFromLocal}
							className={`w-full ${validation.errors.effectiveDates ? 'p-invalid' : ''}`}
						/>
						<small className={validation.errors.effectiveDates ? 'p-error' : 'text-xs text-500'}>
							{validation.errors.effectiveDates || 'The selected dates apply to the recurring weekday hours below.'}
						</small>
					</div>
					<div className="col-12 md:col-4 machine-operational-schedule-indefinite-column">
						<div className="machine-operational-schedule-indefinite">
							<InputSwitch
								inputId="operational-schedule-runs-indefinitely"
								checked={value.runsIndefinitely}
								onChange={(event) => setRunsIndefinitely(event.value)}
							/>
							<label htmlFor="operational-schedule-runs-indefinitely" className="text-sm text-600">Runs indefinitely</label>
						</div>
					</div>
				</div>
			</div>

			<div className="machine-operational-schedule-grid">
				<div className="machine-operational-schedule-header text-xs font-medium text-500 uppercase">
					<span>Day</span>
					<span>Opening hours</span>
					<span>Overnight</span>
				</div>
				{OPERATIONAL_SCHEDULE_DAYS.map(({ key, label }) => {
					const window = windowsByDay.get(key);
					const error = validation.errors[key];
					return (
						<div key={key} className="machine-operational-schedule-row">
							<div className="machine-operational-schedule-day">
								<InputSwitch
									inputId={`operational-schedule-${key}`}
									checked={Boolean(window)}
									onChange={(event) => setDayEnabled(key, event.value)}
								/>
								<label htmlFor={`operational-schedule-${key}`} className="text-sm font-medium text-700">
									{label}
								</label>
							</div>
							<div className="machine-operational-schedule-times">
								<InputText
									type="time"
									value={window?.opensAtLocal || ''}
									disabled={!window}
									onChange={(event) => updateWindow(key, { opensAtLocal: event.target.value })}
									className={error ? 'p-invalid' : ''}
								/>
								<span className="text-500">to</span>
								<InputText
									type="time"
									value={window?.closesAtLocal || ''}
									disabled={!window}
									onChange={(event) => updateWindow(key, { closesAtLocal: event.target.value })}
									className={error ? 'p-invalid' : ''}
								/>
								{error && <small className="p-error machine-operational-schedule-error">{error}</small>}
							</div>
							<div className="machine-operational-schedule-overnight">
								<InputSwitch
									inputId={`operational-schedule-${key}-overnight`}
									checked={Boolean(window?.endsNextDay)}
									disabled={!window}
									onChange={(event) => updateWindow(key, { endsNextDay: event.value })}
								/>
								<label htmlFor={`operational-schedule-${key}-overnight`} className="text-sm text-600">
									Ends next day
								</label>
							</div>
						</div>
					);
				})}
			</div>
			<MachineOperationalClosures
				timezone={value.timezone}
				scheduleConfigured={!isNew}
				closures={closures}
				loading={closuresLoading}
				loadError={closuresLoadError}
				onCreate={onCreateClosure}
				onUpdate={onUpdateClosure}
				onDelete={onDeleteClosure}
				onRetry={onRetryClosures}
			/>
		</div>
	);
};

export default MachineOperationalScheduleSettings;
